/* eslint-disable max-lines-per-function */
import React from "react";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import { v4 as uuid } from "uuid";
import { FieldArray, useField } from "formik";
import * as yup from "yup";

import { Button } from "components/haven-design";
import { CloseButton } from "components/haven-design/buttons";

import { Handle } from "pages/eligibility/type";
import OutlinedDivContainer from "components/haven-design/container/outlined";
import { formatChildren, keyFromIdentity } from "../helpers";
import Question, { QuestionProps } from "../question";
import { extendWithValidatorNested } from "../../validations";
import OutlinedDiv from "./outlined";
import { getInitialValue } from ".";

export const initial = (
  question: QuestionProps,
  _questionsMap: Record<string, QuestionProps>,
  parent?: string,
  index?: number,
): Record<string, []> => ({
  [keyFromIdentity(question.identity, parent, index)]: [],
});

export const validator = (
  question: QuestionProps,
  questionsMap: Record<string, QuestionProps>,
  parent?: string,
  index?: number,
): Record<string, yup.AnySchema> => {
  const parentName = keyFromIdentity(question.identity, parent, index);
  const children = question.data.children.reduce((childAcc: any, childIdentity: string[]) =>
    extendWithValidatorNested(childAcc, childIdentity, questionsMap), {});
  return {
    [parentName]: yup.array().of(yup.object().shape(children)),
  };
};

const FormQuestionArray =
({ questionsMap, data, name, title }: Handle<"FormQuestionArray"> & { name: string; questionsMap: Record<string, QuestionProps>; data: any; }): React.ReactNode => {
  const [, { value }] = useField(name);
  const children = formatChildren(data.children, questionsMap);
  const childInitials = children.reduce((initials, { params }) => ({
    ...initials,
    ...getInitialValue(params, questionsMap),
  }), {});
  const getDefaults = () => ({ ...childInitials, uniqueID: uuid() });
  return (
    <FieldArray
      name={name}
      render={(helpers) => (
        <OutlinedDiv forceBigText fullWidth label={title || ""}>
          <Grid container spacing={2}>
            {(value || []).map((item: any, index: number) => {
              if (!item) return null;
              const { uniqueID } = item;
              return (
                <Grid key={`${uniqueID}-${index}`} item container xs={12} spacing={1} alignContent="flex-start">
                  <OutlinedDivContainer>
                    <Grid item xs={12}>
                      <IconButton aria-label="delete" size="small"
                        onClick={() => helpers.remove(index)} style={{ float: "right", lineHeight: "1em" }}
                      >
                    &nbsp;<CloseButton onClick={() => null} style={{ float: "right", position: "relative", top: 0, right: "2px" }}/>
                      </IconButton>
                    </Grid>
                    {children.map(({ key, params }) => (
                      <Question
                        key={`${uniqueID}-${key}`}
                        {...params}
                        questionsMap={questionsMap}
                        parent={name}
                        index={index}
                      />
                    ))}
                  </OutlinedDivContainer>
                </Grid>
              );
            })}
            <Grid item xs={12}>
              <Button
                color="secondary"
                type="button"
                fullWidth
                onClick={() => helpers.push(getDefaults())}
              >
                {data.label}
              </Button>
            </Grid>
          </Grid>
        </OutlinedDiv>
      )}
    />
  );
};


FormQuestionArray.initial = initial;
FormQuestionArray.validator = validator;
export default FormQuestionArray;
